/**
 * Misc income receipts (TC fee, old dues …) of one student – shown in Fee Deposit History and Student 360.
 * Renders nothing when the student has none (or the ledger module is not set up yet).
 */
import React, { useEffect, useRef, useState } from "react";
import { Table, Button, Badge } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import schoolApi from "../../api/schoolApi";
import LedgerReceipt from "./LedgerReceipt";
import { inr, fmtDate, num } from "./ledgerUtils";

const StudentMiscReceipts = ({ studentId, variant = "screen", title = "Other Receipts (Misc Income)" }) => {
  const [rows, setRows] = useState([]);
  const [printEntry, setPrintEntry] = useState(null);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current, onAfterPrint: () => setPrintEntry(null) });

  useEffect(() => {
    if (printEntry) setTimeout(() => handlePrint(), 50);
  }, [printEntry]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    if (!studentId) return undefined;
    schoolApi
      .ledgerGet(`/student/${studentId}`)
      .then((r) => alive && setRows((r?.result || []).filter((x) => x.entry_type === "INCOME")))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [studentId]);

  if (!rows.length) return null;
  const total = rows.filter((r) => r.status === "active").reduce((a, r) => a + num(r.amount), 0);

  if (variant === "pdf") {
    const cell = { border: "1px solid #ccc", padding: "4px 6px", fontSize: 12 };
    return (
      <div style={{ marginTop: 15 }}>
        <h5 style={{ background: "#6c757d", color: "white", padding: "8px", borderRadius: "4px", marginBottom: 10 }}>{title}</h5>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Receipt No.", "Date", "Session", "Category", "Mode", "Amount (₹)"].map((h) => (
                <th key={h} style={{ ...cell, background: "#f1f1f1", textAlign: h.startsWith("Amount") ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={r.status === "cancelled" ? { color: "#999", textDecoration: "line-through" } : undefined}>
                <td style={cell}>{r.receipt_number}</td>
                <td style={cell}>{fmtDate(r.entry_date)}</td>
                <td style={cell}>{r.session_year}</td>
                <td style={cell}>{r.category_name}</td>
                <td style={cell}>{r.payment_method}</td>
                <td style={{ ...cell, textAlign: "right" }}>{inr(r.amount)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...cell, fontWeight: "bold" }} colSpan={5}>Total</td>
              <td style={{ ...cell, textAlign: "right", fontWeight: "bold" }}>{inr(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-3 my-3">
      <div className="section-header mb-2">
        <span style={{ color: "black" }}>{title}</span>
      </div>
      <Table striped size="sm" responsive className="data-table">
        <thead>
          <tr>
            <th>Receipt No.</th>
            <th>Date</th>
            <th>Session</th>
            <th>Category</th>
            <th>Mode</th>
            <th className="text-end">Amount (₹)</th>
            <th>Remark</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.status === "cancelled" ? { opacity: 0.55 } : undefined}>
              <td className="fw-semibold">
                {r.receipt_number} {r.status === "cancelled" && <Badge bg="danger">Cancelled</Badge>}
              </td>
              <td>{fmtDate(r.entry_date)}</td>
              <td>{r.session_year}</td>
              <td>{r.category_name}</td>
              <td>{r.payment_method}</td>
              <td className="text-end">{inr(r.amount)}</td>
              <td className="small">{r.remark}</td>
              <td className="text-end">
                <Button size="sm" variant="outline-secondary" title="Print" onClick={() => setPrintEntry(r)}>
                  <i className="fa-solid fa-print" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="fw-bold">
            <td colSpan={5}>Total</td>
            <td className="text-end">{inr(total)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </Table>
      <div style={{ display: "none" }}>
        <LedgerReceipt ref={printRef} entry={printEntry} />
      </div>
    </div>
  );
};

export default StudentMiscReceipts;
